import { forwardRef, useEffect, useState } from "react";
import { PencilIcon } from "@heroicons/react/24/outline";
import ModalFrame from "../../components/ModalFrame";
import { EditPeerReviewGroupForm } from "./EditPeerReviewGroupForm";
import { CoursePromptReviewGroup } from "./coursePromptReviewGroups/coursePromptReviewGroupsApiSlice";
import {
	useLazyEditGroupQuery,
	useEditGroupMutation,
	EditReviewGroupModel,
	ReviewGroupWriterModel,
} from "./peerReviewGroupsApiSlice";

interface EditPeerReviewGroupButtonProps {
	onClick: () => void;
}

export const EditPeerReviewGroupButton = forwardRef<
	HTMLButtonElement,
	EditPeerReviewGroupButtonProps
>((props, ref) => {
	function handleClick() {
		props.onClick();
	}

	return (
		<button
			ref={ref}
			className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
			onClick={handleClick}>
			<PencilIcon className="size-4" />
			<span className="ml-2">Edit</span>
		</button>
	);
});

interface EditPeerReviewGroupDialogProps {
	rowId: number | string;
	isDialogOpen: boolean;
	coursePromptGroup?: CoursePromptReviewGroup;
	availablePool?: ReviewGroupWriterModel[];
	onEditCoursePromptGroup?: (newGroup: CoursePromptReviewGroup) => void;
	onClose: () => void;
}

export function EditPeerReviewGroupDialog(props: EditPeerReviewGroupDialogProps) {
	const [editGroupData, setEditGroupData] = useState<EditReviewGroupModel>();
	const [callGetEditGroup] = useLazyEditGroupQuery();
	const [callPostEditGroup] = useEditGroupMutation();

	useEffect(() => {
		if (props.isDialogOpen) {
			if (props.coursePromptGroup && props.availablePool) {
				setEditGroupData({
					id: props.coursePromptGroup.id,
					name: props.coursePromptGroup.name,
					writerIds: props.coursePromptGroup.studentIds,
					writerPool: props.availablePool,
				});
			} else {
				callGetEditGroup(Number(props.rowId))
					.unwrap()
					.then((result) => {
						setEditGroupData(result);
					})
					.catch((error: any) => {
						console.log(error);
					});
			}
		}
	}, [props.isDialogOpen]);

	function handleSubmit(model: EditReviewGroupModel) {
		var errorMessage = "";
		if (props.coursePromptGroup) {
			const group: CoursePromptReviewGroup = {
				id: model.id,
				uniqueId: props.rowId.toString(),
				name: model.name,
				studentIds: model.writerIds,
			};

			if (props.onEditCoursePromptGroup) {
				props.onEditCoursePromptGroup(group);
				props.onClose();
			}
		} else {
			callPostEditGroup(model)
				.unwrap()
				.then(() => {
					props.onClose();
				})
				.catch((error: any) => {
					console.log(error);
					errorMessage = error.data;
				});
		}
		return errorMessage;
	}

	return (
		editGroupData && (
			<ModalFrame
				open={props.isDialogOpen}
				title="Edit Review Group"
				overflow="overflow-visible"
				onClose={() => props.onClose()}>
				<EditPeerReviewGroupForm
					initialGroupData={editGroupData}
					submitLabel="Save Group"
					onSubmit={handleSubmit}
					onClose={props.onClose}
				/>
			</ModalFrame>
		)
	);
}
